/**
 * Durable copy of the last staff write, keyed by account **and** the agent who wrote it.
 *
 * Reads by client-restricted staff are gated on the customer having made this account's
 * creditor an offer (per-offer sharing, #3436); operator staff read by ownership, and writes
 * are never gated. `useBudgetAssessment` already papers over that gap with an
 * in-memory `written` slot, but a refresh or a `BudgetPanel` unmount (the tab, not the account,
 * so switching tabs in the Inbox or Task Feed tears it down) clears that slot the same as
 * everything else React holds — at which point the gated reads return nothing and a
 * fully-persisted completion looks deleted. This is `written`'s twin, surviving both.
 *
 * sessionStorage is scoped to the browser tab, not to whoever is logged into it — closing the
 * tab clears it, but signing a different agent in without closing it does not. Every real read
 * this fallback stands in for is scoped by `operator_id` (and, for a sharing check, `client_id`)
 * on the server; a cache keyed on `accountId` alone would skip that the moment a second agent
 * used the same tab, handing them income/expenditure disclosure their own FGA grants may not
 * cover. The WorkOS user id closes that: a different agent reads under a different key and
 * simply misses, exactly as if nothing had ever been written.
 *
 * localStorage would carry the same figures across tab closes and browser restarts for no
 * benefit this fallback needs; sessionStorage plus a TTL bounds both. Every access is guarded —
 * Safari private mode throws on storage access — degrading to "nothing cached" rather than an
 * error.
 *
 * The frozen question set travels with the row, because the row alone cannot carry it: a
 * `StaffAssessment` has no `config_snapshot`, and on precisely the accounts this cache exists
 * for, the detail read that would supply one returns nothing. A rehydrated assessment with no
 * questions sends `BudgetCapture` to its live-config fallback — the client's question set as it
 * stands now, in place of the set the assessment was actually frozen against, which is the one
 * `upsert_answer` enforces.
 */

import type { StaffAssessment } from '@/apps/customers/services/budget-assessments';

const KEY_PREFIX = 'hub:budget-assessment-written:';
const TTL_MS = 24 * 60 * 60 * 1000;

export interface WrittenAssessment {
  assessment: StaffAssessment;
  /** The set frozen when the assessment was started; null when no write has recorded one. */
  questions: string[] | null;
}

interface Entry extends WrittenAssessment {
  storedAt: number;
}

// No user id and no bucket: an identity not yet resolved must not read or write under a key a
// different, later-resolved agent could ever land on too.
function storageKey(accountId: string, userId: string | null): string | null {
  return userId ? `${KEY_PREFIX}${userId}:${accountId}` : null;
}

export function readWrittenAssessment(accountId: string, userId: string | null): WrittenAssessment | null {
  const key = storageKey(accountId, userId);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Entry;
    if (typeof entry?.storedAt !== 'number' || Date.now() - entry.storedAt >= TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    if (!entry.assessment) return null;
    return { assessment: entry.assessment, questions: entry.questions ?? null };
  } catch {
    return null;
  }
}

/**
 * Records the row, and the frozen question set when the caller establishes one.
 *
 * Only `start` knows the frozen set, so every later write omits `questions` and keeps whatever
 * `start` recorded — passing null there instead would erase it on the first answer saved,
 * which is the same defect as never having stored it.
 */
export function writeWrittenAssessment(accountId: string, userId: string | null, assessment: StaffAssessment, questions?: string[]): void {
  const key = storageKey(accountId, userId);
  if (!key) return;
  try {
    const kept = questions ?? readWrittenAssessment(accountId, userId)?.questions ?? null;
    sessionStorage.setItem(key, JSON.stringify({ assessment, questions: kept, storedAt: Date.now() } satisfies Entry));
  } catch {
    // Quota or a disabled store: the tab still has it in memory for the rest of this session.
  }
}

/**
 * Drops every agent's entry for every account.
 *
 * For an operator or market switch, where the JWT and FGA scope change under the *same*
 * person. `user.id` is the WorkOS person and does not move across a `switchToOrganization`,
 * so the per-agent key alone would keep the previous operator's disclosure readable — the
 * cross-operator flash `queryClient.clear()` exists to prevent, arriving through a channel
 * that call does not reach.
 *
 * Keys are collected before removing: removal reindexes the store, so deleting while walking
 * it by index skips entries.
 */
export function clearAllWrittenAssessments(): void {
  try {
    const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i)).filter(
      (key): key is string => !!key?.startsWith(KEY_PREFIX)
    );
    for (const key of keys) sessionStorage.removeItem(key);
  } catch {
    // Quota-free operation on a disabled store: nothing was readable, so nothing to clear.
  }
}

export function clearWrittenAssessment(accountId: string, userId: string | null): void {
  const key = storageKey(accountId, userId);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clear.
  }
}
